// Number formatting that never implies more precision than the data has.

export function compact(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  const a = Math.abs(v)
  if (a >= 1e12) return `${sig(v / 1e12, digits)} trillion`
  if (a >= 1e9) return `${sig(v / 1e9, digits)} billion`
  if (a >= 1e6) return `${sig(v / 1e6, digits)} million`
  if (a >= 1e4) return Math.round(v).toLocaleString('en-US')
  if (a >= 100) return Math.round(v).toLocaleString('en-US')
  if (a >= 10) return sig(v, 3)
  return sig(v, digits + 1)
}

export function sig(v: number, digits = 2): string {
  if (v === 0) return '0'
  const p = Math.pow(10, digits - Math.ceil(Math.log10(Math.abs(v))))
  const r = Math.round(v * p) / p
  return r.toLocaleString('en-US', { maximumFractionDigits: 6 })
}

export function short(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—'
  const a = Math.abs(v)
  if (a >= 1e9) return `${sig(v / 1e9, 2)}B`
  if (a >= 1e6) return `${sig(v / 1e6, 2)}M`
  if (a >= 1e3) return `${sig(v / 1e3, 2)}k`
  return sig(v, 2)
}

export function range(lo: number | null, mid: number | null, hi: number | null, unit = ''): string {
  if (lo === null || hi === null || lo === hi) return `${compact(mid)}${unit ? ' ' + unit : ''}`
  return `${compact(lo)}–${compact(hi)}${unit ? ' ' + unit : ''}`
}

export function mw(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return v >= 1000 ? `${sig(v / 1000, 3)} GW` : `${sig(v, 3)} MW`
}

export function usd(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return '$' + compact(v)
}

export function pct(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined) return '—'
  return `${(v * 100).toFixed(digits)}%`
}

export const STATUS_LABEL: Record<string, string> = {
  operating: 'Operating', construction: 'Under construction', permitted: 'Permitted', proposed: 'Proposed', cancelled: 'Cancelled',
}

export const COOLING_LABEL: Record<string, string> = {
  closed_loop: 'Closed-loop', air: 'Air / dry', evaporative: 'Evaporative', hybrid: 'Hybrid', unknown: 'Not disclosed',
}

export function titleCase(s: string | null | undefined): string {
  if (!s) return ''
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function niceDate(s: string | null | undefined): string {
  if (!s) return '—'
  if (/^\d{4}$/.test(s)) return s
  // date-only strings are calendar dates, not instants: build them in local time so they don't shift a day west of UTC
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(s)
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, m[3] ? Number(m[3]) : 1) : new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: m && !m[3] ? undefined : 'numeric' })
}
